import {
  AuthenticationFailedError,
  isAuthenticationError,
  isNetworkError,
} from "@/auth/AuthError";
import { config } from "@/config";
import { useAuth } from "@/hooks/use-auth";
import { useAppDispatch } from "@/store/useApp";
import {
  Agent,
  AgentBoard,
  AgentFile,
  CreateAgentInput
} from "@/types/agent";
import {
  Assessment,
  AssessmentInfo,
  CreateAssessmentInput,
} from "@/types/assessment";
import { AuthSession, Organization, UserData } from "@/types/auth";
import { AuditStatsResponse } from "@/types/audit";
import { ChatMessage } from "@/types/chat";
import { Course, Student } from "@/types/course";
import {
  Block,
  Document,
  DocumentInfo,
  DocumentStatus,
  SentimentType,
  Suggestion,
} from "@/types/document";
import { GradeDistribution, Grading } from "@/types/grading";
import { InsightsChatRequest, InsightsChatResponse } from "@/types/insights";
import { Rubric } from "@/types/rubric";
import { useCallback } from "react";

type RequestOptions = {
  method?: string;
  headers?: Record<string, string>;
  body?: BodyInit;
  anonymous?: boolean;
  suppressAuthRedirect?: boolean;
};

export const useApi = () => {
  const dispatch = useAppDispatch();

  const { getAccessToken, logout, refreshAccessToken } = useAuth();

  // Internal helper methods for API requests
  const request = useCallback(
    async <T>(endpoint: string, options: RequestOptions = {}): Promise<T> => {
      console.log("🌐 API Request:", {
        endpoint,
        method: options.method || "GET",
        apiUrl: config.apiUrl,
        anonymous: options.anonymous,
      });

      const makeHeaders = (token?: string): Record<string, string> => {
        const h: Record<string, string> = { ...(options.headers || {}) };
        if (!options.anonymous && token) {
          h.Authorization = `Bearer ${token}`;
        }
        return h;
      };

      const getErrorMessage = async (response: Response) => {
        console.error("❌ API Error:", {
          status: response.status,
          statusText: response.statusText,
          url: response.url,
          endpoint,
        });

        let errorMessage =
          response.statusText || `Request failed with status ${response.status}`;
        try {
          const errorData = await response.json();
          console.error("🔍 Error response data:", errorData);
          errorMessage =
            errorData.detail ||
            errorData.message ||
            errorData.error ||
            response.statusText;
        } catch {
          // ignore JSON parse error and use default status text
        }

        return errorMessage;
      };

      const handleError = async (response: Response) => {
        const errorMessage = await getErrorMessage(response);

        if (
          !options.anonymous &&
          (response.status === 401 || response.status === 403)
        ) {
          if (!options.suppressAuthRedirect) {
          logout();
          }
          throw new AuthenticationFailedError(errorMessage);
        }

        const error = new Error(errorMessage) as Error & { status: number };
        error.status = response.status;
        console.error("❌ API request failed:", error);
        throw error;
      };

      const executeRequest = async (token?: string) => {
        return await fetch(`${config.apiUrl}${endpoint}`, {
          ...options,
          headers: makeHeaders(token),
        });
      };

      const initialToken = options.anonymous
        ? undefined
        : await getAccessToken();
      console.log(
        "🔑 Token obtained:",
        initialToken ? "✅ Present" : "❌ Missing"
      );

      try {
        let response = await executeRequest(initialToken);

        if (
          !options.anonymous &&
          (response.status === 401 || response.status === 403)
        ) {
          try {
            const refreshedToken = await refreshAccessToken();
            response = await executeRequest(refreshedToken);
          } catch (error) {
            if (!options.suppressAuthRedirect) {
              logout();
            }
            throw error;
          }
        }

        if (!response.ok) {
          await handleError(response);
        }

        if (response.status === 204) {
          return null as T;
        }

        const data = await response.json();
        console.log("✅ Request successful");
        return data;
      } catch (error) {
        console.error("❌ Request failed:", {
          error: error instanceof Error ? error.message : "Unknown error",
          endpoint,
          apiUrl: config.apiUrl,
        });

        // Re-throw authentication errors from AuthProvider as-is
        if (isAuthenticationError(error)) {
          throw error;
        }

        // Preserve HTTP status errors (these should not be wrapped as network errors)
        if (error instanceof Error && "status" in error) {
          throw error;
        }

        // Check if this is a network error and wrap it appropriately
        if (isNetworkError(error) || error instanceof TypeError) {
          const networkError = new Error(
            `Network error: Unable to connect to ${config.apiUrl}`
          );
          networkError.name = "NetworkError";
          throw networkError;
        }

        throw error;
      }
    },
    [getAccessToken, logout, refreshAccessToken]
  );

  const get = useCallback(
    async <T>(
      endpoint: string,
      options?: { anonymous?: boolean; suppressAuthRedirect?: boolean }
    ) => {
      return await request<T>(endpoint, options);
    },
    [request]
  );

  const post = useCallback(
    async <T>(
      endpoint: string,
      data?: unknown,
      options?: { anonymous?: boolean }
    ) => {
      const requestOptions: RequestOptions = {
        method: "POST",
        headers: {
          ...(!data || !(data instanceof FormData)
            ? { "Content-Type": "application/json" }
            : {}),
        },
        body: data instanceof FormData ? data : JSON.stringify(data),
        anonymous: options?.anonymous ?? false,
      };
      return await request<T>(endpoint, requestOptions);
    },
    [request]
  );

  const put = useCallback(
    async <T>(endpoint: string, data?: unknown) => {
      const options: RequestOptions = {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      };
      return await request<T>(endpoint, options);
    },
    [request]
  );

  const patch = useCallback(
    async <T>(endpoint: string, data: unknown) => {
      const options: RequestOptions = {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      };
      return await request<T>(endpoint, options);
    },
    [request]
  );

  const del = useCallback(
    async <T>(endpoint: string) => {
      return await request<T>(endpoint, { method: "DELETE" });
    },
    [request]
  );

  const fetchWithAuth = useCallback(
    async (input: string, init: RequestInit = {}) => {
      const executeRequest = async (token: string) => {
        const headers = new Headers(init.headers);
        headers.set("Authorization", `Bearer ${token}`);

        return await fetch(input, {
          ...init,
          headers,
        });
      };

      let response = await executeRequest(await getAccessToken());

      if (response.status === 401 || response.status === 403) {
        try {
          response = await executeRequest(await refreshAccessToken());
        } catch {
          logout();
          throw new AuthenticationFailedError(
            "Your session has expired. Please sign in again."
          );
        }
      }

      if (response.status === 401 || response.status === 403) {
        logout();
        throw new AuthenticationFailedError(
          "Your session has expired. Please sign in again."
        );
      }

      return response;
    },
    [getAccessToken, logout, refreshAccessToken]
  );

  // Refactored API methods using the helpers
  const fetchDocuments = useCallback(async () => {
    const documents = await get<DocumentInfo[]>("/documents");
    dispatch({ type: "SET_DOCUMENTS", payload: documents });
  }, [dispatch, get]);

  const fetchDocument = useCallback(
    async (id: string) => {
      const response = await post<Document>(`/documents/${id}`);
      dispatch({ type: "SET_CURRENT_DOCUMENT", payload: response });
    },
    [dispatch, post]
  );

  const importDocument = useCallback(
    async (file: File, assessmentId: string | null) => {
      const formData = new FormData();
      formData.append("file", file);
      if (assessmentId) {
        formData.append("assessment_id", assessmentId);
      }

      const document = await post<DocumentInfo>(
        "/documents/import?transform=md",
        formData
      );
      dispatch({ type: "ADD_DOCUMENT", payload: document });
      return document;
    },
    [dispatch, post]
  );

  const updateBlock = useCallback(
    async (
      documentId: string,
      blockId: string,
      updates: { sentiment?: SentimentType | null }
    ) => {
      await patch<Block>(`/documents/${documentId}/blocks/${blockId}`, updates);
      dispatch({
        type: "UPDATE_BLOCK_SENTIMENT",
        payload: { blockId: blockId, sentiment: updates.sentiment || null },
      });
    },
    [dispatch, patch]
  );

  const approveSuggestion = useCallback(
    async (documentId: string, suggestion: Suggestion) => {
      await put(
        `/documents/${documentId}/suggestions/${suggestion.id}/approve`
      );
      dispatch({
        type: "UPDATE_SUGGESTION",
        payload: { ...suggestion, status: "approved" },
      });

      if (suggestion.primaryBlockId) {
        dispatch({
          type: "UPDATE_BLOCK_SENTIMENT",
          payload: { blockId: suggestion.primaryBlockId, sentiment: suggestion.sentiment || null },
        });
      }
    },
    [dispatch, put]
  );

  const updateSuggestion = useCallback(
    async (documentId: string, suggestion: Suggestion) => {
      await put(
        `/documents/${documentId}/suggestions/${suggestion.id}`,
        suggestion
      );
      dispatch({ type: "UPDATE_SUGGESTION", payload: suggestion });
    },
    [dispatch, put]
  );

  const rejectSuggestion = useCallback(
    async (documentId: string, suggestion: Suggestion) => {
      await put(`/documents/${documentId}/suggestions/${suggestion.id}/reject`);
      dispatch({
        type: "UPDATE_SUGGESTION",
        payload: { ...suggestion, status: "rejected" },
      });
    },
    [dispatch, put]
  );

  const deleteSuggestion = useCallback(
    async (documentId: string, suggestionId: string) => {
      await del(`/documents/${documentId}/suggestions/${suggestionId}`);
      dispatch({ type: "DELETE_SUGGESTION", payload: suggestionId });
    },
    [dispatch, del]
  );

  const createSuggestion = useCallback(
    async (documentId: string, suggestion: Suggestion) => {
      const createdSuggestion = await post<Suggestion>(
        `/documents/${documentId}/suggestions`,
        suggestion
      );
      dispatch({ type: "CREATE_SUGGESTION", payload: createdSuggestion });
    },
    [dispatch, post]
  );

  const fetchGrading = useCallback(
    async (documentId: string) => {
      const grading = await get<Grading>(`/grading/${documentId}`);
      dispatch({ type: "SET_GRADING", payload: grading }, true);
      return grading;
    },
    [dispatch, get]
  );

  const fetchGradeDistribution = useCallback(
    async (assessmentId: string) => {
      const data = await get<GradeDistribution>(
        `/grading/assessments/${assessmentId}/stats`
      );
      return data;
    },
    [get]
  );

  const fetchAuditStats = useCallback(async () => {
    return await get<AuditStatsResponse>("/audit");
  }, [get]);

  const askInsights = useCallback(
    async (params: InsightsChatRequest) => {
      return await post<InsightsChatResponse>("/insights/chat", params);
    },
    [post]
  );

  const fetchRubric = useCallback(
    async (documentId: string) => {
      const rubric = await get<Rubric>(`/documents/${documentId}/rubric`);
      dispatch({ type: "SET_CURRENT_RUBRIC", payload: rubric });
      return rubric;
    },
    [dispatch, get]
  );

  const fetchDocumentUrl = useCallback(
    async (documentId: string): Promise<string | null> => {
      const response = await get<{ url: string | null }>(
        `/documents/${documentId}/url`
      );
      return response.url;
    },
    [get]
  );

  const loadPdf = useCallback(
    async (documentId: string) => {
      const response = await fetchWithAuth(
        `${config.apiUrl}/documents/${documentId}/pdf`
      );
      if (!response.ok) {
        throw new Error("Failed to load PDF");
      }
      const blob = await response.blob();
      return URL.createObjectURL(blob);
    },
    [fetchWithAuth]
  );

  const fetchRawMedia = useCallback(
    async (url: string): Promise<Blob> => {
      const response = await fetchWithAuth(
        `${config.apiUrl}/media/raw?url=${encodeURIComponent(url)}`
      );
      if (!response.ok) {
        throw new Error("Failed to fetch raw media");
      }
      return await response.blob();
    },
    [fetchWithAuth]
  );

  const fetchChatMessages = useCallback(
    async (documentId: string) => {
      const messages = await get<ChatMessage[]>(
        `/chats/${documentId}/messages`
      );
      dispatch({ type: "SET_CHAT_MESSAGES", payload: messages });
      return messages;
    },
    [get, dispatch]
  );

  const previewGrading = useCallback(
    async (documentId: string) => {
      // Update onboarding
      dispatch({ type: "SET_ONBOARDING", payload: { generate: true } });

      const grading = await post<Grading>(`/grading/${documentId}`);
      dispatch({ type: "SET_GRADING", payload: grading });
      return grading;
    },
    [dispatch, post]
  );

  const sendChatMessage = useCallback(
    async (documentId: string, message: string, blockId?: string | null) => {
      // Add user message to chat
      dispatch({
        type: "ADD_CHAT_MESSAGE",
        payload: {
          role: "user",
          message: message,
          createdAt: new Date().toISOString(),
        },
      });

      const response = await fetchWithAuth(
        `${config.apiUrl}/chats/${documentId}/messages`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            message,
            blockId,
          }),
        }
      );

      if (!response.ok) {
        throw new Error(
          `API request failed: ${response.status} ${response.statusText}`
        );
      }

      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error("No response body");
      }

      const decoder = new TextDecoder();
      let accumulatedMessage = "";

      try {
        while (true) {
          const { done, value } = await reader.read();

          if (done) {
            break;
          }

          const chunk = decoder.decode(value, { stream: true });

          // Accumulate the text chunk
          accumulatedMessage += chunk;

          // Update the assistant message in real-time
          dispatch({
            type: "UPDATE_CHAT_MESSAGE",
            payload: {
              role: "assistant",
              message: accumulatedMessage,
              createdAt: new Date().toISOString(),
            },
          });
        }
      } finally {
        reader.releaseLock();
      }

      return {
        role: "assistant" as const,
        message: accumulatedMessage,
        createdAt: new Date().toISOString(),
      };
    },
    [dispatch, fetchWithAuth]
  );

  const clearChatMessages = useCallback(
    async (documentId: string) => {
      await del(`/chats/${documentId}`);
      dispatch({ type: "CLEAR_CHAT_MESSAGES" });
    },
    [del, dispatch]
  );

  const createCourse = useCallback(
    async (courseData: { title: string; reference?: string; semester?: string }) => {
      const course = await post<Course>("/courses", courseData);
      dispatch({ type: "ADD_COURSE", payload: course });
      return course;
    },
    [post, dispatch]
  );

  const updateCourse = useCallback(
    async (course: Course) => {
      await put<Course>(`/courses/${course.id}`, course);
      dispatch({ type: "UPDATE_COURSE", payload: course });
    },
    [dispatch, put]
  );

  const fetchMe = useCallback(async (options?: { suppressAuthRedirect?: boolean }) => {
    const userData = await get<UserData>("/me", options);
    dispatch({ type: "SET_USER_DATA", payload: userData });
    return userData;
  }, [dispatch, get]);

  const fetchCourses = useCallback(async () => {
    const courses = await get<Course[]>("/courses");
    dispatch({ type: "SET_COURSES", payload: courses });
    return courses;
  }, [dispatch, get]);

  const fetchCourseShareInfo = useCallback(
    async (courseId: string) => {
      const response = await get<{ qrCodeUrl: string; shortUrl: string }>(
        `/courses/${courseId}/share`
      );
      dispatch({
        type: "UPDATE_COURSE",
        payload: { id: courseId, ...response },
      });
      return response;
    },
    [dispatch, get]
  );

  const fetchCourseStudents = useCallback(
    async (courseId: string) => {
      return await get<Student[]>(`/courses/${courseId}/students`);
    },
    [get]
  );

  const fetchAssessments = useCallback(async () => {
    const assessments = await get<AssessmentInfo[]>("/assessments");
    dispatch({ type: "SET_ASSESSMENTS", payload: assessments });
  }, [dispatch, get]);

  const parseInstructions = useCallback(
    async (file: File) => {
      const formData = new FormData();
      formData.append("file", file);
      const response = await post<{ markdown: string; filePath: string }>(
        "/assessments/parse/instructions?transform=md",
        formData
      );
      return response;
    },
    [post]
  );

  const parseRubric = useCallback(
    async (file: File) => {
      const formData = new FormData();
      formData.append("file", file);
      const response = await post<Rubric>(
        "/assessments/parse/rubric?transform=md",
        formData
      );
      return response;
    },
    [post]
  );

  const createAssessment = useCallback(
    async (courseId: string, data: CreateAssessmentInput) => {
      const assessment = await post<Assessment>(
        `/courses/${courseId}/assessments`,
        data
      );
      dispatch({
        type: "ADD_ASSESSMENT",
        payload: {
          ...assessment,
          hasSampleSubmission: Boolean(assessment.sampleSubmissionFilePath),
        },
      });
      return assessment;
    },
    [dispatch, post]
  );

  const fetchAssessmentSampleSubmission = useCallback(
    async (assessmentId: string): Promise<string> => {
      const response = await get<{ markdown: string }>(
        `/assessments/${assessmentId}/sample-submission`
      );
      return response.markdown;
    },
    [get]
  );

  const updateAssessment = useCallback(
    async (courseId: string, assessmentId: string, data: AssessmentInfo) => {
      await put<Assessment>(
        `/courses/${courseId}/assessments/${assessmentId}`,
        data
      );
      dispatch({ type: "UPDATE_ASSESSMENT", payload: data });
    },
    [dispatch, put]
  );

  const patchAssessment = useCallback(
    async (
      courseId: string,
      assessmentId: string,
      data: Partial<AssessmentInfo>
    ) => {
      const updated = await patch<AssessmentInfo>(
        `/courses/${courseId}/assessments/${assessmentId}`,
        data
      );
      dispatch({
        type: "UPDATE_ASSESSMENT",
        payload: updated,
      });
    },
    [dispatch, patch]
  );

  const deleteAssessment = useCallback(
    async (courseId: string, assessmentId: string) => {
      await del(`/courses/${courseId}/assessments/${assessmentId}`);
      dispatch({ type: "DELETE_ASSESSMENT", payload: assessmentId });
    },
    [dispatch, del]
  );

  const exportGradingReport = useCallback(
    async (assessmentId: string, type: "grades" | "performance") => {
      const response = await fetchWithAuth(`${config.apiUrl}/grading/export`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ assessmentId, type }),
      });
      if (!response.ok) {
        throw new Error(`Failed to export ${type} report`);
      }

      // Get the filename from the Content-Disposition header or use a default
      const contentDisposition = response.headers.get("Content-Disposition");
      const filename = contentDisposition
        ?.split("filename=")[1]
        ?.replace(/"/g, "");
      if (!filename) {
        throw new Error("No filename provided in Content-Disposition header");
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);

      // Create a temporary link to download the file
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up the URL object
      URL.revokeObjectURL(url);
    },
    [fetchWithAuth]
  );

  const exportAuditReport = useCallback(
    async (assessmentId: string, documentId: string) => {
      const response = await fetchWithAuth(
        `${config.apiUrl}/audit/export/${assessmentId}/${documentId}`,
        {
          method: "GET",
        }
      );
      if (!response.ok) {
        throw new Error(`Failed to export audit report`);
      }

      const contentDisposition = response.headers.get("Content-Disposition");
      const filename =
        contentDisposition?.split("filename=")[1]?.replace(/"/g, "") ||
        `audit-report-${documentId}.pdf`;

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      URL.revokeObjectURL(url);
    },
    [fetchWithAuth]
  );

  const exportAuditBundle = useCallback(
    async (assessmentId: string) => {
      const response = await fetchWithAuth(
        `${config.apiUrl}/audit/export/${assessmentId}`,
        {
        method: "GET",
        }
      );
      if (!response.ok) {
        throw new Error("Failed to export audit bundle");
      }

      const contentDisposition = response.headers.get("Content-Disposition");
      const filename =
        contentDisposition?.split("filename=")[1]?.replace(/"/g, "") ||
        `audit-export-${assessmentId}.zip`;

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      URL.revokeObjectURL(url);
    },
    [fetchWithAuth]
  );

  const exportFeedback = useCallback(
    async (documentId: string, format: "pdf" | "markdown") => {
      const response = await fetchWithAuth(
        `${config.apiUrl}/grading/${documentId}/feedback/export?format=${format}`,
        {
          method: "GET",
        }
      );
      if (!response.ok) {
        throw new Error(`Failed to export feedback as ${format}`);
      }

      // Get the filename from the Content-Disposition header or use a default
      const contentDisposition = response.headers.get("Content-Disposition");
      const filename =
        contentDisposition?.split("filename=")[1]?.replace(/"/g, "") ||
        `feedback.${format === "pdf" ? "pdf" : "md"}`;

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);

      // Create a temporary link to download the file
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up the URL object
      URL.revokeObjectURL(url);
    },
    [fetchWithAuth]
  );

  const updateDocument = useCallback(
    async (
      documentId: string,
      updates: { status?: DocumentStatus; notes?: string }
    ) => {
      if (updates.notes || updates.notes === "") {
        dispatch({
          type: "UPDATE_DOCUMENT_NOTES",
          payload: { id: documentId, notes: updates.notes },
        });
      }
      const document = await patch(`/documents/${documentId}`, updates);
      if (updates.status) {
        dispatch({
          type: "UPDATE_DOCUMENT_STATUS",
          payload: { id: documentId, status: updates.status },
        });
      }
      return document;
    },
    [dispatch, patch]
  );

  const deleteDocument = useCallback(
    async (documentId: string) => {
      await del(`/documents/${documentId}`);
      dispatch({ type: "DELETE_DOCUMENT", payload: documentId });
    },
    [dispatch, del]
  );

  const resetDocument = useCallback(
    async (documentId: string) => {
      const response = await post<{ success: boolean }>(
        `/documents/${documentId}/reset`
      );
      dispatch({
        type: "UPDATE_DOCUMENT_STATUS",
        payload: {
          id: documentId,
          status: response.success ? "ready" : "in_progress",
        },
      });
      return response;
    },
    [post, dispatch]
  );

  const updateFinalGrade = useCallback(
    async (documentId: string, grade: string) => {
      await put(`/grading/${documentId}/`, { grade });
      dispatch({ type: "UPDATE_FINAL_GRADE", payload: grade });
    },
    [dispatch, put]
  );

  const publishFeedback = useCallback(
    async (documentId: string, summary: string, gradingId: string) => {
      const response = await post<{ id: string; publishedAt: string }>(
        `/grading/${documentId}/publish`,
        { feedback: summary }
      );

      // Update feedback in state
      dispatch({
        type: "UPDATE_FEEDBACK",
        payload: {
          id: gradingId,
          feedback: {
            status: "published",
            publishedAt: response.publishedAt,
          },
        },
      });

      // Update document status
      dispatch({
        type: "UPDATE_DOCUMENT_STATUS",
        payload: { id: documentId, status: "done" },
      });

      return response;
    },
    [post, dispatch]
  );

  const saveFeedback = useCallback(
    async (documentId: string, summary: string) => {
      const response = await put<Grading>(`/grading/${documentId}/feedback`, {
        feedback: summary,
      });

      // Update grading in state with the updated model
      dispatch({
        type: "SET_GRADING",
        payload: response,
      });

      return response;
    },
    [put, dispatch]
  );

  const fetchDocumentData = useCallback(
    async (documentId: string) => {
      await fetchDocument(documentId);
      await fetchGrading(documentId);
      await fetchRubric(documentId);
      await fetchChatMessages(documentId);
    },
    [fetchDocument, fetchGrading, fetchRubric, fetchChatMessages]
  );

  // Auth-related methods
  const startAuth = useCallback(
    async (email: string, organizationId?: string) => {
      return await post(
        "/auth/start",
        {
          email,
          role: "user",
          ...(organizationId ? { organizationId } : {}),
        },
        { anonymous: true }
      );
    },
    [post]
  );

  const verifyAuth = useCallback(
    async (email: string, otpCode: string) => {
      return await post<AuthSession>(
        "/auth/verify",
        {
          email,
          otp_code: otpCode,
          role: "user",
        },
        { anonymous: true }
      );
    },
    [post]
  );

  const exchangeLtiCode = useCallback(
    async (code: string) => {
      return await post<AuthSession>(
        "/lti/exchange",
        { code },
        { anonymous: true }
      );
    },
    [post]
  );

  const deleteOnboarding = useCallback(async () => {
    await del("/me/onboarding");
    dispatch({ type: "DELETE_ONBOARDING" });
  }, [dispatch, del]);

  const completeOnboarding = useCallback(async () => {
    await put("/me/onboarding", {});
    dispatch({ type: "COMPLETE_ONBOARDING" });
  }, [dispatch, put]);

  const updateUserNotes = useCallback(
    async (notes: string) => {
      dispatch({ type: "UPDATE_USER_NOTES", payload: notes });
      await put("/me/notes", { notes });
    },
    [dispatch, put]
  );

  const updateSettings = useCallback(
    async (settings: { semesters: string[] }) => {
      dispatch({ type: "UPDATE_USER_SETTINGS", payload: settings });
      await put("/me/settings", settings);
    },
    [dispatch, put]
  );

  const uploadFile = useCallback(
    async (file: File) => {
      const formData = new FormData();
      formData.append("file", file);
      const response = await post<{ path: string }>(
        "/uploads?transform=md",
        formData
      );
      return response.path;
    },
    [post]
  );

  const uploadMedia = useCallback(
    async (file: File, transformToMd: boolean = true) => {
      const formData = new FormData();
      formData.append("file", file);
      const url = transformToMd ? "/media/upload?transform=md" : "/media/upload";
      const response = await post<{
        path: string;
        filename: string;
        contentType: string;
      }>(url, formData);
      return response;
    },
    [post]
  );

  const transcribeMedia = useCallback(
    async (file: Blob) => {
      const formData = new FormData();
      const fileName =
        file instanceof File ? file.name : `recording-${Date.now()}.webm`;
      formData.append("file", file, fileName);
      return await post<{ text: string }>("/media/transcribe", formData);
    },
    [post]
  );

  const evaluateSuggestion = useCallback(
    async (documentId: string, note: string, blockId?: string) => {
      return await post<{
        criteriaId?: string;
        sentiment?: SentimentType;
      }>(`/documents/${documentId}/suggestions/evaluate`, { note, blockId });
    },
    [post]
  );

  const lookupOrganization = useCallback(
    async (code: string) => {
      return await get<Organization>(`/organizations/lookup?code=${code}`, {
        anonymous: true,
      });
    },
    [get]
  );

  const completeLtiDeepLink = useCallback(
    async (data: { deep_link_session_id: string; assessment_id: string }) => {
      const response = await fetchWithAuth(
        `${config.apiUrl}/lti/deep-link/complete`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(data),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to complete LTI deep link");
      }

      return await response.text();
    },
    [fetchWithAuth]
  );

  // Agent methods
  const createAgent = useCallback(
    async (data: CreateAgentInput) => {
      const agent = await post<Agent>("/agents", data);
      dispatch({ type: "ADD_AGENT", payload: agent });
      return agent;
    },
    [dispatch, post]
  );

  const updateAgent = useCallback(
    async (id: string, data: Agent) => {
      await put<Agent>(`/agents/${id}`, data);
      dispatch({ type: "UPDATE_AGENT", payload: data });
    },
    [dispatch, put]
  );

  const deleteAgent = useCallback(
    async (id: string) => {
      await del(`/agents/${id}`);
      dispatch({ type: "DELETE_AGENT", payload: id });
    },
    [dispatch, del]
  );

  const previewAgent = useCallback(
    async (agentId: string, documentId: string): Promise<string> => {
      const result = await post<string>(`/agents/${agentId}/preview`, {
        documentId,
      });
      return result;
    },
    [post]
  );

  const previewAgentPlaybook = useCallback(
    async (data: {
      type: string;
      instructions: string;
      files: AgentFile[];
      boards: AgentBoard[];
      documentId?: string;
      assessmentId?: string;
    }): Promise<string> => {
      return await post<string>("/agents/preview", data);
    },
    [post]
  );

  return {
    fetchDocuments,
    fetchDocument,
    importDocument,
    updateDocument,
    deleteDocument,
    resetDocument,
    updateBlock,
    approveSuggestion,
    updateSuggestion,
    rejectSuggestion,
    deleteSuggestion,
    createSuggestion,
    fetchGrading,
    fetchGradeDistribution,
    fetchAuditStats,
    askInsights,
    fetchRubric,
    fetchDocumentUrl,
    loadPdf,
    fetchRawMedia,
    fetchChatMessages,
    sendChatMessage,
    clearChatMessages,
    createCourse,
    updateCourse,
    fetchMe,
    fetchCourses,
    fetchAssessments,
    createAssessment,
    parseInstructions,
    parseRubric,
    fetchAssessmentSampleSubmission,
    updateAssessment,
    deleteAssessment,
    exportGradingReport,
    exportAuditReport,
    exportAuditBundle,
    exportFeedback,
    updateFinalGrade,
    publishFeedback,
    saveFeedback,
    previewGrading,
    fetchDocumentData,
    uploadFile,
    uploadMedia,
    transcribeMedia,
    evaluateSuggestion,

    // Auth methods
    startAuth,
    verifyAuth,
    exchangeLtiCode,
    completeLtiDeepLink,
    deleteOnboarding,
    completeOnboarding,
    updateUserNotes,
    updateSettings,
    lookupOrganization,

    // Agent methods
    createAgent,
    updateAgent,
    deleteAgent,
    previewAgent,
    previewAgentPlaybook,
    fetchCourseStudents,
    fetchCourseShareInfo,
    patchAssessment,
  };
};
